// Copyright (c) Docugami, Inc. All rights reserved.

import { useEffect, useRef } from "react";

/**
 * Helper hook for setInterval that ensures the callback that is triggered
 * is always the callback from the latest render. Using setInterval from
 * a hook will otherwise result in old state from the first render being
 * referenced.
 * @param callback The callback to trigger
 * @param delay The delay between callbacks
 */
export function useInterval(callback: () => void, delay?: number) {
    const savedCallback = useRef(callback);

    // Remember the latest callback.
    useEffect(() => {
        savedCallback.current = callback;
    }, [callback]);

    // Set up the interval.
    useEffect(() => {
        function tick() {
            savedCallback.current();
        }

        if (delay !== undefined) {
            const id = setInterval(tick, delay);
            return () => clearInterval(id);
        }
    }, [delay]);
}
